import jQuery from '../../utils/fl-jquery';
import utils from '../../utils/utils';
import config from '../../config';
import Request from '../request';
import SVGInjector from '../../utils/svg-injector';
import { analyticsTracker as Tracker, logger } from '@findologic/js-common';
import { transformSuggestResponse } from './helpers';
import { EventNames } from '../../../tscoba/src/Common/enums.ts';

/**
 * Initializes the mobile Smart Suggest.
 *
 * @param {{}} settings
 * @param {string} [settings.chromeHtml] Overlay base structure.
 * @param {string} [settings.placeholderHtml] Placeholder to show if no suggestions are available, e.g. no query or
 * no results.
 * @param {string} [settings.suggestionHtml] Structure of an individual suggestion.
 * @param {[]} [settings.suggestionTypes] Types of suggestions to generate. Defaults to text suggestions only.
 * @param {int} [settings.suggestionCount] Number of suggestions to request; defaults to 8.
 * @param {string} [settings.noReultsText] Text that is displayed if the query does not match any results.
 * @param {string} [settings.type] Type of algorithm to use for suggestion generation.
 * @param {string} [settings.window] Reference to the window object for accessing global properties. Should be used
 * for tests only. If not set, the global window object is used.
 * @returns {MobileSmartSuggest}
 * @constructor
 */

// eslint-disable-next-line complexity
function MobileSmartSuggest(settings) {
    if (!(this instanceof MobileSmartSuggest)) {
        return new MobileSmartSuggest(settings);
    }
    const self = this;
    this.recognition;
    const HIDDEN_INPUT_CLASS = 'extra-hidden-inputs';

    const countryKeys = {
        de: 'de-DE',
        en: 'en-GB',
        it: 'it-IT',
        fr: 'fr-FR',
        es: 'es-ES',
        nl: 'nl-NL',
        pl: 'pl-PL',
        ru: 'ru-RU',
        sv: 'sv-SE',
        hu: 'hu-HU',
        cz: 'cs-CZ',
        pt: 'pt-PT',
        da: 'da-DK',
        no: 'nb-NO',
        cn: 'cmn-Hans-CN',
        ja: 'ja-JP',
        sl: 'sl-SI',
    };

    const SUGGESTION_KINDS = Object.freeze({
        SUGGEST: 'suggest',
        TOP_QUERIES: 'topQueries',
    });

    this.extraVoiceParams = { flSearchTrigger: 'mobileVoice' };

    // Apply default settings.
    this.settings = jQuery.extend(
        {
            chromeHtml:
                '<div class="fl-mobile-smart-suggest">\n' +
                '    <form autocomplete="off" action="#">\n' +
                '        <div class="fl-controls">\n' +
                '            <button class="fl-back">\n' +
                '                <img class="fl-back-icon fl-svg" data-src="{0}" data-fallback="{1}">\n' +
                '            </button>\n' +
                '            <input aria-label="search" type="search" name="query" placeholder="{8}">\n' +
                '            <button class="fl-clear" style="display: none;">\n' +
                '                <img class="fl-clear-icon fl-svg" data-src="{2}" data-fallback="{3}">\n' +
                '            </button>\n' +
                '            <button class="fl-record" style="display: none;">\n' +
                '                <img class="fl-record-icon fl-svg" data-src="{6}" data-fallback="{7}">\n' +
                '            </button>\n' +
                '            <button type="submit" class="fl-submit" title="search">\n' +
                '                <img class="fl-search-icon fl-svg" data-src="{4}" data-fallback="{5}">\n' +
                '            </button>\n' +
                '        </div>\n' +
                '    </form>\n' +
                '    <div class="fl-errors"></div>\n' +
                '    <div class="fl-suggestions"></div>\n' +
                '</div>',
            suggestionHtml:
                '<li class="fl-suggestion fl-regular-suggestion">\n' +
                '    <span class="fl-suggestion-label">{0}</span>\n' +
                '    <span class="fl-suggestion-use">\n' +
                '        <img class="fl-suggestion-icon fl-svg" data-src="{1}" data-fallback="{2}">\n' +
                '    </span>\n' +
                '</li>',
            errorHtml: '<li class="fl-error">\n' + '    <span class="fl-error-label">{0}</span>\n' + '</li>',
            suggestionTypes: [config.mobileSmartSuggest.config.suggestType],
            suggestionCount: 8,
            noResultsText: '{0} <a href="#">{1}</a>',
            placeholderText: '{0}',
            type: settings.type || 'result_v3',
            window,
        },
        settings
    );

    /**
     * Shows mobile Smart Suggest when focus is set on the triggering search field, but only if there isn't one
     * already.
     *
     * @param {object} e Is used to determine the triggering search field, so it can be filled with the query when
     * submitting.
     */
    function focusListener(e) {
        if (jQuery('.fl-mobile-smart-suggest').length < 1) {
            self.show(jQuery(e.target));
        }
    }

    /**
     * Focus the already clicked element after the mobile Smart Suggest is loaded.
     */
    function focusAfterInit() {
        const focusedElement = document.activeElement;

        if (focusedElement.hasAttribute('autofocus')) {
            focusedElement.removeAttribute('autofocus');
            focusedElement.blur();

            return;
        }

        const searchFieldContainingFocusedElement = utils.getSearchFieldElements().toArray().indexOf(focusedElement);
        if (searchFieldContainingFocusedElement !== -1) {
            jQuery(utils.getSearchFieldElements()[searchFieldContainingFocusedElement]).trigger('focus');
        }
    }

    /**
     * Gets the mobile Smart Suggest search field.
     *
     * @returns {jQuery} Search field element.
     */
    function getOverlaySearchField() {
        return jQuery('.fl-mobile-smart-suggest input[name=query]');
    }

    function initializeSpeechRecognition() {
        self.recognition = new self.settings.window.webkitSpeechRecognition();
        let recognizedWords = '';
        self.recognition.continuous = true;
        self.recognition.interimResults = true;
        self.recognition.lang = countryKeys[config.language];

        self.recognition.onstart = function () {
            self.toggleErrorMessage(false);
            self.toggleRecordButtonColor(false);
        };

        // This event is triggered when the speech recognition service returns a result
        // IsFinal checka if a word or phrase has been positively recognized
        // and this has been communicated back to us
        self.recognition.onresult = function (event) {
            for (let i = event.resultIndex; i < event.results.length; i++) {
                // results is a array with a alternatives given by the api, we are always using [i][0],
                // because it gave the best results
                if (event.results[i].isFinal) {
                    recognizedWords = event.results[i][0].transcript;
                }
                self.updateOverlaySearchField(recognizedWords);
                // Suggestions should not be shown, as they provide no added value while speaking.
            }
        };
        self.recognition.onend = function () {
            self.toggleRecordButtonColor(true);
            const searchField = getOverlaySearchField();

            if (recognizedWords !== '') {
                self.toggleClearButton(true);
                self.submitAndHide(searchField, self.extraVoiceParams);
            }
        };
    }

    this.triggeringSearchField = undefined;

    utils.getSearchFieldElements().focus(focusListener);

    /**
     * Shows the mobile Smart Suggest with all elements and the entire behavior.
     *
     * @param {jQuery} triggeringSearchField It is used to determine where to set the query when submitting and
     * closing the overlay.
     */
    this.show = function (triggeringSearchField) {
        const iconBaseUrl = `${config.cdnBaseUrl}/autocomplete/img/`;
        const suggestButtonBackIcon = `${iconBaseUrl}suggest_button_arrow.svg`;
        const suggestButtonBackIconFallback = `${iconBaseUrl}suggest_button_arrow.png`;
        const suggestButtonClearIcon = `${iconBaseUrl}suggest_button_clear.svg`;
        const suggestButtonClearIconFallback = `${iconBaseUrl}suggest_button_clear.png`;
        const suggestButtonSearchIcon = `${iconBaseUrl}suggest_button_search.svg`;
        const suggestButtonSearchIconFallback = `${iconBaseUrl}suggest_button_search.png`;
        const suggestButtonRecordIcon = `${iconBaseUrl}suggest_button_record.svg`;
        const suggestButtonRecordIconFallback = `${iconBaseUrl}suggest_button_record.png`;
        const mobileSmartSuggestTextConfig = config.mobileSmartSuggest.text;

        // Set the has to leverage history support for swiping on iOS and the Android back button.
        window.location.hash = '#suggest:text';

        this.triggeringSearchField = triggeringSearchField;

        // Add SVGs for the controls and translations.
        const overlay = jQuery(
            utils.formatString(
                this.settings.chromeHtml,
                suggestButtonBackIcon,
                suggestButtonBackIconFallback,
                suggestButtonClearIcon,
                suggestButtonClearIconFallback,
                suggestButtonSearchIcon,
                suggestButtonSearchIconFallback,
                suggestButtonRecordIcon,
                suggestButtonRecordIconFallback,
                mobileSmartSuggestTextConfig.searchPlaceholder
            )
        );
        this.settings.placeholderText = utils.formatString(this.settings.placeholderText, mobileSmartSuggestTextConfig.searchPlaceholder);
        this.settings.noResultsText = utils.formatString(
            this.settings.noResultsText,
            mobileSmartSuggestTextConfig.noResults,
            mobileSmartSuggestTextConfig.showRelatedResults
        );
        SVGInjector(overlay.find('.fl-svg'));

        // Get all hidden input fields from the triggering search field, so we can append them to the
        // mobile Smart Suggest.
        const form = triggeringSearchField.parent();
        form.children('input[type=hidden]').each(function () {
            jQuery(this).clone().appendTo(jQuery(overlay).find('form'));
        });

        const body = jQuery('body');
        this.installChromeHandlers(overlay);

        body.append(overlay);
        if (self.recognition !== undefined && triggeringSearchField.val() === '') {
            self.showRecordButton();
        } else {
            self.hideRecordButton();
        }

        this.updateQueryIfExists(overlay);

        setTimeout(function () {
            overlay.css('opacity', 1);
        }, 50);

        // Prevent scrolling under the overlay.
        body.css('overflow', 'hidden');

        // Not only focus the search field once, but do so continuously until closing to ensure that focus is not
        // stolen by site scripts.
        const overlaySearchField = overlay.find('input[name=query]');
        overlaySearchField.focus();
        this.searchFieldFocusInterval = setInterval(function () {
            if (!overlaySearchField.is(':focus')) {
                overlaySearchField.focus();
            }
        }, 500);
    };

    /**
     * Check if the original query exists and set the mobile Smart Suggest query to it.
     *
     * @param {jQuery} overlay Overlay element containing the required input field.
     */
    this.updateQueryIfExists = function (overlay) {
        let query = this.triggeringSearchField.val().trim();
        const placeholder = this.settings.placeholderText;

        if (query === placeholder) {
            query = '';
        }

        overlay.find('input[name=query]').val(query);

        if (query.length > 0) {
            this.updateSuggestions(query, this.settings.suggestionTypes);
            this.toggleClearButton(true);
        } else if (config.mobileSmartSuggest.config.suggestionsForEmptyQuery !== null) {
            this.showSuggestionsForEmptyQuery();
        }
    };

    this.submitAndHide = function (searchField, extraParams) {
        this.updateTriggeringSearchField(searchField.val());
        this.submit(self.triggeringSearchField, extraParams);
        this.hide();

        return false;
    };

    /**
     * Installs all event handlers to the mobile Smart Suggest.
     *
     * @param {jQuery} overlay Overlay element containing interactive elements that event listeners should be
     * added to.
     */
    this.installChromeHandlers = function (overlay) {
        overlay.find('.fl-back').click(function () {
            self.hide();

            return false;
        });

        const searchField = overlay.find('input[name=query]');
        searchField.keyup(function (e) {
            const query = jQuery(e.target).val().trim();
            if (query.length > 0) {
                self.hideRecordButton();
                self.toggleClearButton(true);
                self.toggleErrorMessage(false);
                self.updateSuggestions(query, self.settings.suggestionTypes);
            } else {
                self.showSuggestionsForEmptyQuery();
                if (self.recognition !== undefined) {
                    self.showRecordButton();
                }
            }
        });

        searchField.keydown(function (e) {
            if (e.keyCode === 13) {
                e.preventDefault();

                return self.submitAndHide(searchField);
            }
        });

        overlay.find('form').submit(function () {
            self.submitAndHide(searchField);

            return false;
        });

        overlay.find('.fl-clear').click(function () {
            if (self.recognition !== undefined) {
                self.showRecordButton();
            }
            self.updateOverlaySearchField('');
            self.showSuggestionsForEmptyQuery();
            getOverlaySearchField().focus();

            return false;
        });

        overlay.find('.fl-suggestions').click(function () {
            self.updateTriggeringSearchField(searchField.val());
            self.hide();

            return false;
        });

        let recording = false;
        overlay.find('.fl-record').click(function () {
            if (recording) {
                self.recognition.stop();
                recording = false;
                if (getOverlaySearchField().val() !== '') {
                    return self.submitAndHide(searchField, self.extraVoiceParams);
                }
            } else {
                self.updateOverlaySearchField('');
                self.clearSuggestions();
                self.recognition.start();
                recording = true;

                self.recognition.onerror = function (event) {
                    if (event.error === 'not-allowed') {
                        const error = config.mobileSmartSuggest.text.voiceSearchError;
                        const errorHtml = jQuery(utils.formatString(self.settings.errorHtml, error));

                        const responseHtml = jQuery('<ul>');

                        responseHtml.append(errorHtml);
                        jQuery('.fl-mobile-smart-suggest .fl-errors').html(responseHtml);

                        self.toggleErrorMessage(true);
                    }

                    self.recognition.stop();
                    recording = false;
                };
            }

            getOverlaySearchField().focus();

            return false;
        });

        this.hashchangeListener = function () {
            if (window.location.hash.indexOf('#suggest:text') < 0) {
                self.hide();
            }
        };
        jQuery(window).on('hashchange', this.hashchangeListener);
    };

    /**
     * Installs event handlers for all suggestions.
     *
     * @param {jQuery} suggestionsHtml Element where the event handlers should be prepended on.
     */
    this.installSuggestionHandlers = function (suggestionsHtml) {
        suggestionsHtml.find('.fl-suggestion-use').click(function (e) {
            const suggestion = jQuery(e.target).closest('.fl-suggestion').prop('fl-suggestion');

            self.updateOverlaySearchField(`${suggestion.label} `);
            self.updateSuggestions(suggestion.label, self.settings.suggestionTypes);

            // Restore focus on the search field, so the user can continue typing.
            getOverlaySearchField().focus();

            e.stopImmediatePropagation();

            return false;
        });

        suggestionsHtml.find('.fl-suggestion').click(function (e) {
            const suggestion = jQuery(e.target).closest('.fl-suggestion').prop('fl-suggestion');
            const query = jQuery('.fl-controls input[name=query]').val();

            self.updateOverlaySearchField(suggestion.noresult ? query : suggestion.label);
            self.updateTriggeringSearchField(suggestion.noresult ? query : suggestion.label);

            const submitCallback = function () {
                self.submit(self.triggeringSearchField);
            };
            self.trackClick(query, suggestion, submitCallback);
            self.hide();
        });
    };

    /**
     * Tracks Mobile Smart Suggest clicks using a tracking service available in the shop. If none is available,
     * nothing will happen.
     *
     * @param {string} query The entered query that led to the suggestions.
     * @param {{}} suggestion The data of the selected item.
     * @param {string} suggestion.block (Internal) name of the block the suggestion appeared in.
     * @param {string} suggestion.label Visible text of the selected item.
     * @param {function} callback Called once tracking is complete.
     */
    this.trackClick = function (query, suggestion, callback) {
        const tracker = Tracker.getInstance(config, config.trackSmartSuggest);

        const label = suggestion.label;
        let readableBlockName;
        switch (suggestion.block) {
            case SUGGESTION_KINDS.SUGGEST:
                readableBlockName = 'Suggestion';
                break;
            case SUGGESTION_KINDS.TOP_QUERIES:
                readableBlockName = 'Top Query';
                break;
            default:
                readableBlockName = suggestion.block ? suggestion.block : 'Suggestion';
        }

        const category = `Smart Suggest Click: ${readableBlockName}`;
        tracker.trackEvent('findologicEvent', category, label, query, callback, {
            event: EventNames.MOBILE_SMART_SUGGEST_CLICK,
            block: suggestion.block,
            query,
        });
    };

    /**
     * Hides the mobile Smart Suggest.
     * *Note: It does not only hide, but removes the HTML from it completely.*
     */
    this.hide = function () {
        if (this.recognition !== undefined) {
            self.recognition.stop();
        }
        // Add any typed text to the triggering search field, so it's not lost for the user.
        const query = getOverlaySearchField().val();
        if (typeof query !== 'undefined' && typeof this.triggeringSearchField !== 'undefined' && query.length > 0) {
            this.triggeringSearchField.val(query);
        }

        // Stop the interval that ensures overlay search field focus.
        clearInterval(this.searchFieldFocusInterval);

        // Remove the hashchange listener to prevent confusion in the future.
        jQuery(window).off('hashchange', this.hashchangeListener);
        window.location.hash = '';

        // Stop blocking scrolling on the shop underneath the overlay.
        jQuery('body').css('overflow', 'initial');

        const overlay = jQuery('.fl-mobile-smart-suggest');

        overlay.css('opacity', 0);

        setTimeout(function () {
            overlay.remove();
        }, 50);
    };

    /**
     * Updates suggestions based on the given query.
     *
     * @param {string} query Searched query.
     * @param suggestionTypes
     */
    this.updateSuggestions = function (query, suggestionTypes) {
        const request = new Request({
            onSuccess(response) {
                // In case the response was proxied, it may be returned as text, so we have to parse it first.
                if (typeof response !== 'object') {
                    response = JSON.parse(response);
                }

                // If the response does not deliver any results, we manipulate the result that it will display a
                // no-results text.
                if (response.length === 0 && query.length !== 0) {
                    response = [{ label: self.settings.noResultsText, noresult: true }];
                }

                const suggestionsHtml = self.responseToHtml(transformSuggestResponse(self.settings.type, query, response), query);
                self.installSuggestionHandlers(suggestionsHtml);

                jQuery('.fl-mobile-smart-suggest .fl-suggestions').html(suggestionsHtml);
            },
            onError() {
                self.clearSuggestions();
            },
            onTimeout() {
                self.clearSuggestions();
            },
        });

        const params = {};
        jQuery.each(jQuery('.fl-mobile-smart-suggest form').serializeArray(), function (key, param) {
            params[param.name] = param.value;
        });

        // Those params can not be overwritten.
        const defaultParams = {
            query,
            q: query,
            type: this.settings.type,
            autocompleteblocks: suggestionTypes,
            count: this.settings.suggestionCount,
        };

        request.send(Request.TYPE_SUGGEST, jQuery.extend({}, params, defaultParams));

        // Scroll to top when suggestions have been updated.
        jQuery('.fl-suggestions').scrollTop(0);
    };

    /**
     * Clears the suggestions and hides the clear button.
     */
    this.showSuggestionsForEmptyQuery = function () {
        self.toggleClearButton(false);
        self.updateSuggestions('', [config.mobileSmartSuggest.config.suggestionsForEmptyQuery]);
    };

    this.clearSuggestions = function () {
        self.toggleClearButton(false);
        jQuery('.fl-mobile-smart-suggest .fl-suggestions > *').remove();
    };

    /**
     * Sets the value of the mobile Smart Suggest search field input to the given query.
     * *Note: It also scrolls to the last character of the search field, to ensure that the rightmost part of the
     * text is always visible, so the user can continue typing with confidence in knowing what they are typing after.*
     *
     * @param {string} query Searched query.
     */
    this.updateOverlaySearchField = function (query) {
        getOverlaySearchField().val(query);

        // Ensure that the rightmost part of the text is always visible, so the user can continue typing with
        // confidence in knowing what they are typing after.
        getOverlaySearchField()[0].setSelectionRange(query.length, query.length);
    };

    /**
     * Sets the value of the shop's search field input to the given query.
     *
     * @param {string} query Searched query.
     */
    this.updateTriggeringSearchField = function (query) {
        this.triggeringSearchField.val(query);
    };

    // TODO: Handle the css changes in a css file
    /**
     * Toggles the clear button's opacity.
     *
     * @param {boolean} show
     */
    this.toggleClearButton = function (show) {
        jQuery('.fl-mobile-smart-suggest .fl-clear').css('display', show ? 'block' : 'none');
    };

    // TODO: Handle the css changes in a css file
    /**
     * Toggles the error message's visibility
     *
     * @param {boolean} show
     */
    this.toggleErrorMessage = function (show) {
        jQuery('.fl-errors').css('display', show ? 'block' : 'none');
    };

    // TODO: Handle the css changes in a css file
    const recordButtonSelektor = '.fl-mobile-smart-suggest .fl-record';
    this.showRecordButton = function () {
        jQuery(recordButtonSelektor).css('display', 'block');
    };
    // TODO: Handle the css changes in a css file
    this.hideRecordButton = function () {
        jQuery(recordButtonSelektor).css('display', 'none');
    };

    // TODO: Handle the css changes in a css file
    /**
     * Toggles Record Button's color.
     *
     * @param {boolean} show
     */
    this.toggleRecordButtonColor = function (show) {
        jQuery('.fl-mobile-smart-suggest .fl-record-icon').css('fill', show ? '#949494' : '#B82E09');
    };

    /**
     * Submits the form of the shop's search field.
     *
     * @param {jQuery} triggeringSearchField Shop's search input field.
     * @param {Object} extraParams Extra params to be added to the form.
     */
    this.submit = function (triggeringSearchField, extraParams) {
        if (extraParams) {
            this.createExtraHidden(extraParams);
        }
        triggeringSearchField.closest('form').submit();
        this.removeExtraHidden();
    };

    /**
     * Converts the response JSON from the server to HTML. It uses the query to highlight text that the user
     * has not entered yet.
     *
     * @param {Object} suggestions Response from the server.
     * @param query Searched query.
     * @returns {jQuery} Converted HTML.
     */
    this.responseToHtml = function (suggestions, query) {
        const iconBaseUrl = `${config.cdnBaseUrl}/autocomplete/img/`;
        const useSuggestionIcon = `${iconBaseUrl}suggest_button_arrow.svg`;
        const useSuggestionIconFallback = `${iconBaseUrl}suggest_button_arrow.png`;
        const html = jQuery('<ul>');

        jQuery.each(suggestions, function (i, suggestion) {
            const suggestionHtml = jQuery(
                utils.formatString(self.settings.suggestionHtml, suggestion.label, useSuggestionIcon, useSuggestionIconFallback)
            );

            suggestionHtml.prop('fl-suggestion', suggestion);

            if (suggestion.noresult === true) {
                // If there are no results, the no-results class will be added.
                jQuery(suggestionHtml).addClass('fl-no-results-suggestion');
                jQuery(suggestionHtml).removeClass('fl-regular-suggestion');
            } else {
                jQuery(suggestionHtml).find('span.fl-suggestion-label')[0].innerHTML = self.createSuggestionCompletion(suggestion, query);
            }

            html.append(suggestionHtml);
        });

        SVGInjector(html.find('.fl-svg'));

        return html;
    };

    /**
     * Converts the response JSON from the server to HTML. It uses the query to highlight text that the user
     * has not entered yet. There is no highlighting when the suggestion doesn't match (includes) the query.
     *
     * @param {Object} suggestion Single response from the server.
     * @param query Searched query.
     * @returns {String} formatted HTML.
     */
    this.createSuggestionCompletion = function (suggestion, query) {
        // umlauts - letters - numbers - comma - whitespace
        const allowedRegex = /[^\u00C0-\u017FA-Z0-9, ]/gi;
        let cleanQuery = query.replace(allowedRegex, ' '); // Remove special characters
        cleanQuery = cleanQuery.replace(/\s+(?=\s)/g, ''); // Remove multiple spaces
        cleanQuery = cleanQuery.trim(); // Remove last and first space if present

        const includesRegex = new RegExp(cleanQuery, 'i');
        if (includesRegex.test(suggestion.label)) {
            const completion = suggestion.label.substring(cleanQuery.length, suggestion.label.length);
            const searchString = suggestion.label.substring(0, cleanQuery.length);

            return `${searchString}<span class='fl-completion'>${utils.escapeHtmlSpecialChars(completion)}</span>`;
        } else {
            return `${utils.escapeHtmlSpecialChars(suggestion.label)}<span class='fl-completion'></span>`;
        }
    };

    /**
     * Destroys the mobile Smart Suggest. It also removes the focus event listener.
     */
    this.destroy = function () {
        this.triggeringSearchField = undefined;
        utils.getSearchFieldElements().off('focus', focusListener);
        this.hide();
    };

    /**
     * Creates hidden input fields for extra parameters
     *
     * @param {Object} extraParams Object with extra fields to be created.
     */
    this.createExtraHidden = function (extraParams) {
        for (const key in extraParams) {
            if (extraParams.hasOwnProperty(key)) {
                jQuery('<input>')
                    .attr({
                        type: 'hidden',
                        name: key,
                        class: HIDDEN_INPUT_CLASS,
                        value: extraParams[key],
                    })
                    .appendTo(this.triggeringSearchField.closest('form'));
            }
        }
    };

    /**
     * Remove all hidden input fields created for extra parameters
     */
    this.removeExtraHidden = function () {
        this.triggeringSearchField.closest('form').find(`.${HIDDEN_INPUT_CLASS}`).remove();
    };

    if (
        config.mobileSmartSuggest.config.voiceSearchEnabled &&
        utils.getProtocol() === 'https' &&
        countryKeys.hasOwnProperty(config.language) &&
        'webkitSpeechRecognition' in this.settings.window &&
        typeof this.settings.window.webkitSpeechRecognition !== 'undefined'
    ) {
        initializeSpeechRecognition();
    } else if (utils.getProtocol() !== 'https') {
        logger.info('Voice search is disabled because the page protocol is not https.');
    }

    // Check for already clicked inputs after everything is loaded
    focusAfterInit();
}

export default MobileSmartSuggest;
